import React, { useState, useEffect, useCallback } from 'react';
import { branchesAPI } from '../../services/api';
import { formatDate, formatCurrency, getInitials } from '../../utils/helpers';
import StatCard from '../../components/common/StatCard';
import Modal from '../../components/common/Modal';
import SearchBar from '../../components/common/SearchBar';
import toast from 'react-hot-toast';
import { institutionAPI } from "../../services/api";

/* ── colour maps ─────────────────────────────────────────────────── */
const BR_STA = { active: { bg: '#d1fae5', c: '#065f46' }, inactive: { bg: '#fee2e2', c: '#991b1b' }, coming_soon: { bg: '#fef3c7', c: '#92400e' }, closed: { bg: '#f1f5f9', c: '#475569' } };
const TRF_STA = { pending: { bg: '#fef3c7', c: '#92400e' }, approved: { bg: '#eff6ff', c: '#1d4ed8' }, in_transit: { bg: '#fdf4ff', c: '#7c3aed' }, completed: { bg: '#d1fae5', c: '#065f46' }, cancelled: { bg: '#fee2e2', c: '#991b1b' }, rejected: { bg: '#fef2f2', c: '#b91c1c' } };
const BOARD_CFG = { CBSE: { bg: '#eff6ff', c: '#1d4ed8' }, ICSE: { bg: '#fdf4ff', c: '#7c3aed' }, IGCSE: { bg: '#f0fdfa', c: '#0f766e' }, IB: { bg: '#fff7ed', c: '#c2410c' }, state: { bg: '#f1f5f9', c: '#475569' } };

const Chip = ({ map, val }) => {
  const c = map?.[val] || { bg: '#f1f5f9', c: '#475569' };
  return <span style={{ background: c.bg, color: c.c, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{(val || '').replace(/_/g, ' ')}</span>;
};

/* ── blank form objects ──────────────────────────────────────────── */
const BLANK_BR = {
  name: '', code: '', tagline: '', address: '', locality: '', city: '', state: '', pincode: '', country: 'India', phone: '', alternate_phone: '', email: '', website: '',
  principal_name: '', principal_email: '', principal_phone: '', vice_principal_name: '', admin_manager_name: '',
  board: '', medium: '', classes_offered: '', established_year: '', affiliation_number: '',
  total_capacity: 0, total_students: 0, total_staff: 0, total_classrooms: 0,
  status: 'active', is_headquarters: false,
  has_hostel: false, has_transport: false, has_library: false, has_labs: false, has_sports_complex: false, has_cafeteria: false,
  can_manage_own_fees: false, can_manage_own_curriculum: false, can_manage_own_staff: false, timezone_offset: '+05:30',
};
const BLANK_TRF = { entity_type: 'student', entity_id: '', entity_name: '', entity_code: '', from_branch: '', to_branch: '', transfer_date: new Date().toISOString().split('T')[0], effective_date: '', reason: '' };
const BLANK_STAFF_ASSIGN = { branch: '', staff_id: '', staff_name: '', role_at_branch: 'teacher', is_primary_branch: true, start_date: new Date().toISOString().split('T')[0] };

export default function BranchManagement() {
  const [tab, setTab] = useState('branches');
  const [branches, setBranches] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [staffAssignments, setStaffAssignments] = useState([]);
  const [branchStats, setBranchStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchPerformance, setBranchPerf] = useState(null);

  /* filters */
  const [search, setSearch] = useState('');
  const [staF, setStaF] = useState('');
  const [trfStaF, setTrfStaF] = useState('');
  const [entityF, setEntityF] = useState('');

  /* modals */
  const [showBrForm, setShowBrForm] = useState(false);
  const [showTrfForm, setShowTrfForm] = useState(false);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editBranch, setEditBranch] = useState(null);
  const [brForm, setBrForm] = useState(BLANK_BR);
  const [trfForm, setTrfForm] = useState(BLANK_TRF);
  const [staffForm, setStaffForm] = useState(BLANK_STAFF_ASSIGN);

  //* institution_information start _____________________________________________ */
  const [isEditing, setIsEditing] = useState(false);
  const [institutionId, setInstitutionId] = useState(null);
  const [mainTab, setMainTab] = useState('institution_information');
  const [institutionForm, setInstitutionForm] = useState({
    institution_name: "",
    founder_name: "",
    affiliation: "",
    shortcode: "",
    institution_type: "",
    package_type: "",
    office_no: "",
    number_of_branches: "",
    email: "",
    alternate_email: "",
    pan_no: "",
    contact_no: "",
    office_address: "",
    logo: null,      // will hold URL string (from API or blob preview)
    logoFile: null,  // will hold actual File object for upload
  });

  useEffect(() => {
    const loadInstitution = async () => {
      try {
        const res = await institutionAPI.get();

        // Handle both { data: {...} } and { data: { data: {...} } } and { data: { results: [...] } }
        const raw = res?.data?.data || res?.data?.results?.[0] || res?.data || null;

        console.log("Institution API response:", res); // ← check this in console
        console.log("Parsed raw data:", raw);          // ← and this

        if (raw && raw.id) {
          setInstitutionForm({
            institution_name: raw.institution_name || "",
            founder_name: raw.founder_name || "",
            affiliation: raw.affiliation || "",
            shortcode: raw.shortcode || "",
            institution_type: raw.institution_type || "",
            package_type: raw.package_type || "",
            office_no: raw.office_no || "",
            number_of_branches: raw.number_of_branches || "",
            email: raw.email || "",
            alternate_email: raw.alternate_email || "",
            pan_no: raw.pan_no || "",
            contact_no: raw.contact_no || "",
            office_address: raw.office_address || "",
            logo: raw.logo || null,  // URL from API
            logoFile: null,
          });
          setInstitutionId(raw.id);
          setIsEditing(false); // data exists → lock fields
        } else {
          setIsEditing(true); // no data yet → allow filling
        }
      } catch (err) {
        console.error("Failed to load institution:", err);
        setIsEditing(true); // on error → allow filling
      }
    };
    loadInstitution();
  }, []);

  const handleSaveInstitution = async () => {

    if (!institutionForm.institution_name.trim()) {
        alert("Institution Name is required");
        return;
    }

    if (!institutionForm.email.trim()) {
        alert("Email is required");
        return;
    }

    if (!institutionForm.contact_no.trim()) {
        alert("Contact No. is required");
        return;
    }


    try {

        const formData = new FormData();

        Object.keys(institutionForm).forEach((key) => {

            if (key !== "logo" && key !== "logoFile") {
                formData.append(key, institutionForm[key] || "");
            }

        });


        if (institutionForm.logoFile) {
            formData.append("logo", institutionForm.logoFile);
        }


        let response;


        if (institutionId) {

            response = await institutionAPI.update(
                institutionId,
                formData
            );

        } else {

            response = await institutionAPI.create(
                formData
            );

        }


        console.log("Save Response:", response);


        // get saved data
        const savedData =
            response?.data?.data ||
            response?.data;


        if(savedData?.id){

            setInstitutionId(savedData.id);

            setInstitutionForm({

                institution_name: savedData.institution_name || "",
                founder_name: savedData.founder_name || "",
                affiliation: savedData.affiliation || "",
                shortcode: savedData.shortcode || "",
                institution_type: savedData.institution_type || "",
                package_type: savedData.package_type || "",
                office_no: savedData.office_no || "",
                number_of_branches: savedData.number_of_branches || "",
                email: savedData.email || "",
                alternate_email: savedData.alternate_email || "",
                pan_no: savedData.pan_no || "",
                contact_no: savedData.contact_no || "",
                office_address: savedData.office_address || "",

                logo: savedData.logo || institutionForm.logo,

                logoFile:null

            });

        }


        setIsEditing(false);


        toast.success(
            institutionId 
            ? "Institution Updated Successfully"
            : "Institution Saved Successfully"
        );


    } catch(error){

        console.log(error);

        toast.error(
            "Failed to save institution"
        );

    }

};
  //* institution_information end _____________________________________________ */

  //* branch management start _____________________________________________ */
  const BF = (k, v) => setBrForm(f => ({ ...f, [k]: v }));
  const TF = (k, v) => setTrfForm(f => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    const bp = {}; if (search) bp.search = search; if (staF) bp.status = staF;
    const tp = {}; if (trfStaF) tp.status = trfStaF; if (entityF) tp.entity_type = entityF;
    Promise.all([
      branchesAPI.list(bp), branchesAPI.stats(),
      branchesAPI.transfers(tp), branchesAPI.staffAssignments({ page_size: 50 }),
    ]).then(([br, st, tf, sa]) => {
      setBranches(br.data.results || br.data);
      setBranchStats(st.data || {});
      setTransfers(tf.data.results || tf.data);
      setStaffAssignments(sa.data.results || sa.data);
    }).catch(() => toast.error('Failed to load branch data')).finally(() => setLoading(false));
  }, [search, staF, trfStaF, entityF]);

  useEffect(() => { load(); }, [load]);

  /* load branch performance */
  useEffect(() => {
    if (selectedBranch) {
      branchesAPI.performance(selectedBranch.id).then(r => setBranchPerf(r.data)).catch(() => { });
    }
  }, [selectedBranch]);

  /* ─── save handlers ─────────────────────────────────────────────── */
  const saveBranch = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editBranch ? await branchesAPI.update(editBranch.id, brForm) : await branchesAPI.create(brForm);
      toast.success(editBranch ? 'Branch updated!' : 'Branch created!');
      setShowBrForm(false); setEditBranch(null); setBrForm(BLANK_BR); load();
    } catch (err) { toast.error(err.response?.data?.detail || Object.values(err.response?.data || {}).flat()[0] || 'Failed'); } finally { setSaving(false); }
  };

  const saveTransfer = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await branchesAPI.createTransfer(trfForm);
      toast.success('Transfer request created!'); setShowTrfForm(false); setTrfForm(BLANK_TRF); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const saveStaffAssign = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await branchesAPI.createStaffAssignment(staffForm);
      toast.success('Staff assigned!'); setShowStaffForm(false); setStaffForm(BLANK_STAFF_ASSIGN); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const approveTransfer = async (id) => {
    const effectiveDate = window.prompt('Effective date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!effectiveDate) return;
    try { await branchesAPI.approveTransfer(id, { effective_date: effectiveDate }); toast.success('Transfer approved!'); load(); }
    catch { toast.error('Failed to approve'); }
  };

  const rejectTransfer = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try { await branchesAPI.rejectTransfer(id, { reason }); toast.success('Transfer rejected.'); load(); }
    catch { toast.error('Failed'); }
  };

  const openEditBranch = br => {
    setEditBranch(br);
    setBrForm({
      name: br.name, code: br.code, tagline: br.tagline || '', address: br.address, locality: br.locality || '', city: br.city, state: br.state, pincode: br.pincode, country: br.country, phone: br.phone, alternate_phone: br.alternate_phone || '', email: br.email || '', website: br.website || '',
      principal_name: br.principal_name || '', principal_email: br.principal_email || '', principal_phone: br.principal_phone || '', vice_principal_name: br.vice_principal_name || '', admin_manager_name: br.admin_manager_name || '',
      board: br.board || '', medium: br.medium || '', classes_offered: br.classes_offered || '', established_year: br.established_year || '', affiliation_number: br.affiliation_number || '',
      total_capacity: br.total_capacity, total_students: br.total_students, total_staff: br.total_staff, total_classrooms: br.total_classrooms,
      status: br.status, is_headquarters: br.is_headquarters,
      has_hostel: br.has_hostel, has_transport: br.has_transport, has_library: br.has_library, has_labs: br.has_labs, has_sports_complex: br.has_sports_complex, has_cafeteria: br.has_cafeteria,
      can_manage_own_fees: br.can_manage_own_fees, can_manage_own_curriculum: br.can_manage_own_curriculum, can_manage_own_staff: br.can_manage_own_staff, timezone_offset: br.timezone_offset,
    });
    setShowBrForm(true);
  };

  const statCards = [
    { icon: '🏛️', label: 'Total Branches', value: branchStats.total_branches || branches.length, color: 'blue' },
    { icon: '✅', label: 'Active', value: branchStats.active || branches.filter(b => b.status === 'active').length, color: 'green' },
    { icon: '🎓', label: 'Total Students', value: branchStats.total_students || branches.reduce((a, b) => a + b.total_students, 0), color: 'purple' },
    { icon: '👨‍🏫', label: 'Total Staff', value: branchStats.total_staff || branches.reduce((a, b) => a + b.total_staff, 0), color: 'orange' },
    { icon: '🏙️', label: 'Cities', value: branchStats.cities || new Set(branches.map(b => b.city)).size, color: 'teal' },
    { icon: '🔄', label: 'Pending Transfers', value: branchStats.pending_transfers || transfers.filter(t => t.status === 'pending').length, color: 'yellow' },
  ];

  const TABS = [
    { key: 'branches', label: '🏛️ Branches', count: branches.length },
    { key: 'comparison', label: '📊 Comparison', count: null },
    { key: 'transfers', label: '🔄 Transfers', count: transfers.length },
    { key: 'staff_assignments', label: '👤 Staff Assignments', count: staffAssignments.length },
  ];


  /* ____ branch user access start _________________________________*/
  const [accessFilters, setAccessFilters] = useState({
    country: '',
    state: '',
    district: '',
    sub_district: '',
    user_type: '',
    branch: '',
    access_type: 'assigned'
  });

  const [branchAccessUsers, setBranchAccessUsers] = useState([
    {
      id: 1,
      branch_name: 'DemoSchool',
      user_type: 'SUPERADMIN',
      user_name: 'Kalam',
      email: 'DemoSchool@sadm.com',
      department: 'ADMIN - Institution Super Admin',
      is_default_branch: true,
      is_current_branch: true,
      unassigned: false
    },

    {
      id: 2,
      branch_name: 'DemoSchool',
      user_type: 'STAFF',
      user_name: 'Bhaanu',
      email: 'bhaanu@staff.com',
      department: 'Teaching - Teacher',
      is_default_branch: true,
      is_current_branch: true,
      unassigned: false
    },

    {
      id: 3,
      branch_name: 'DemoSchool',
      user_type: 'BRANCHADMIN',
      user_name: 'Thiran',
      email: 'thiran@branch.com',
      department: 'ADMIN - Branch Admin',
      is_default_branch: true,
      is_current_branch: true,
      unassigned: false
    },

    {
      id: 4,
      branch_name: 'DemoSchool',
      user_type: 'STAFF',
      user_name: 'Vaishnavi',
      email: 'vaishnavi@staff.com',
      department: 'Teaching - Teacher',
      is_default_branch: true,
      is_current_branch: true,
      unassigned: false
    },

    {
      id: 5,
      branch_name: 'DemoSchool',
      user_type: 'STAFF',
      user_name: 'Anil',
      email: 'anil@staff.com',
      department: 'Dormitory - Hostel Incharge',
      is_default_branch: true,
      is_current_branch: true,
      unassigned: false
    }
  ]);
  /* ____ branch user access end _________________________________*/



  return (
    <div>
      {/* new tabs start */}


      <div className="d-flex gap-2 mb-4 flex-wrap">
        {[
          [
            'institution_information',
            <>
              <i className="bi bi-building fs-5 lh-0"></i>
              Institution Information
            </>
          ],

          [
            'branch_registration',
            <>
              <i className="bi bi-diagram-3 fs-5 lh-0"></i>
              Branch Registration
            </>
          ],

          [
            'branch_user_access',
            <>
              <i className="bi bi-people fs-5 lh-0"></i>
              Branch User Access
            </>
          ]
        ].map(([k, l]) => (
          <button
            key={k}
            className={`btn ${mainTab === k ? 'btn-primary page-btn' : 'btn-outline-secondary page-btn'}`}
            onClick={() => setMainTab(k)}
          >
            {l}
          </button>
        ))}
      </div>
      {/* new tabs end */}


      {/* institution impormation start */}
      {mainTab === 'institution_information' && (
        <div className="card border-0 shadow-sm">

          <div className="card-header justify-content-start">
            <i className="bi bi-building me-2"></i>
            Institution Details
          </div>

          <div className="card-body">
            <div className="row g-3">

              {/* Logo Upload */}
              <div className="col-lg-3">
                <div style={{ border: '1px solid #dee2e6', borderRadius: 10, padding: 10, height: '100%', background: '#fff' }}>
                  <div style={{ height: 250, border: '1px dashed #cbd5e1', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#f8fafc' }}>
                    {institutionForm.logo ? (
                      <img src={institutionForm.logo} alt="Institution Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <div className="text-center text-muted">
                        <i className="bi bi-image fs-1"></i>
                        <div className="mt-2">Upload Institution Logo</div>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    className="form-control mt-3"
                    accept="image/*"
                    disabled={!isEditing}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setInstitutionForm(f => ({ ...f, logoFile: file, logo: URL.createObjectURL(file) }));
                      }
                    }}
                  />
                </div>
              </div>

              {/* Institution Form */}
              <div className="col-lg-9">
                <div className="row g-3">

                  {/* Row 1 */}
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Institution Name <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.institution_name}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, institution_name: e.target.value }))}
                      placeholder="Enter School name"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Founder Name</label>
                    <input
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.founder_name}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, founder_name: e.target.value }))}
                      placeholder="Founder Name"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Affiliation</label>
                    <input
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.affiliation}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, affiliation: e.target.value }))}
                    />
                  </div>

                  {/* Row 2 */}
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Institution Shortcode</label>
                    <input
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.shortcode}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, shortcode: e.target.value }))}
                      placeholder="SE1"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Institution Type</label>
                    <select
                      className="form-select"
                      disabled={!isEditing}
                      value={institutionForm.institution_type}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, institution_type: e.target.value }))}
                    >
                      <option value="">Select Type</option>
                      <option value="school">School</option>
                      <option value="college">College</option>
                      <option value="university">University</option>
                      <option value="institute">Institute</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Package Type</label>
                    <input
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.package_type}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, package_type: e.target.value }))}
                      placeholder="Premium"
                    />
                  </div>

                  {/* Row 3 */}
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Office No.</label>
                    <input
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.office_no}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, office_no: e.target.value }))}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">No. of Branch</label>
                    <input
                      type="number"
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.number_of_branches}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, number_of_branches: e.target.value }))}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Email <span className="text-danger">*</span></label>
                    <input
                      type="email"
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.email}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="schoolname@gmail.com"
                    />
                  </div>

                  {/* Row 4 */}
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Alternate Email</label>
                    <input
                      type="email"
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.alternate_email}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, alternate_email: e.target.value }))}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">PAN No</label>
                    <input
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.pan_no}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, pan_no: e.target.value }))}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Contact No. <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.contact_no}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, contact_no: e.target.value }))}
                      placeholder="9876543210"
                    />
                  </div>

                  {/* Address */}
                  <div className="col-12">
                    <label className="form-label fw-semibold">Office Address</label>
                    <textarea
                      rows={3}
                      className="form-control"
                      disabled={!isEditing}
                      value={institutionForm.office_address}
                      onChange={(e) => setInstitutionForm(f => ({ ...f, office_address: e.target.value }))}
                    />
                  </div>

                  {/* Documents */}
                  <div className="col-12">
                    <div style={{ border: '1px solid #dee2e6', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ background: '#1a3a5c', color: '#fff', padding: '10px 14px', fontWeight: 700 }}>
                        Institution Documents
                      </div>
                      <div className="p-3">
                        <div className="row g-2 align-items-center">
                          <div className="col-md-8">
                            <input className="form-control" placeholder="File Description" disabled={!isEditing} />
                          </div>
                          <div className="col-md-4">
                            <input type="file" className="form-control" disabled={!isEditing} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>

          {/* Footer — buttons change based on mode */}
          <div className="card-footer text-center gap-3 py-3">
            {!isEditing ? (
              // VIEW MODE → Edit button
              <button
 className="btn btn-warning px-4"
 onClick={() => setIsEditing(true)}
>
    <i className="bi bi-pencil me-2"></i>
    Edit
</button>
            ) : (
              // EDIT MODE → Save / Update button
              <button
                className="btn btn-primary px-4"
                onClick={handleSaveInstitution}
              >
                <i className="bi bi-save me-2"></i>
                {institutionId ? "Update Institution" : "Save Institution"}
              </button>
            )}
          </div>

        </div>
      )}
      {/* institution impormation end */}


      {/* branch_registration start */}
      {mainTab === 'branch_registration' && (
        <>
          {/* ── Header ────────────────────────────────────────────────── */}
          <div className="page-header">
            <div>
              <h2 style={{ margin: 0, fontWeight: 800 }}>🏛️ Branch Management</h2>
              <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Multi-campus management — branches, transfers, settings & inter-branch reporting</p>
            </div>
            <div className="d-flex gap-2">
              {tab === 'transfers' && <button className="btn btn-outline-primary" onClick={() => setShowTrfForm(true)}>🔄 Request Transfer</button>}
              {tab === 'staff_assignments' && <button className="btn btn-outline-primary" onClick={() => setShowStaffForm(true)}>👤 Assign Staff</button>}
              <button className="btn btn-primary" onClick={() => { setEditBranch(null); setBrForm(BLANK_BR); setShowBrForm(true); }}>➕ Add Branch</button>
            </div>
          </div>

          {/* ── Stats ─────────────────────────────────────────────────── */}
          <div className="row g-3 mb-4">
            {statCards.map(s => (
              <div key={s.label} className="col-6 col-md-4 col-lg-2">
                <StatCard icon={s.icon} label={s.label} value={s.value} color={s.color} />
              </div>
            ))}
          </div>

          {/* ── Tab bar ───────────────────────────────────────────────── */}
          <div className="d-flex gap-2 mb-4 flex-wrap">
            {TABS.map(t => (
              <button key={t.key} className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(t.key)}>
                {t.label}{t.count !== null && <span className="badge ms-1" style={{ background: tab === t.key ? 'rgba(255,255,255,0.3)' : '#e2e8f0', color: tab === t.key ? '#fff' : '#475569' }}>{t.count}</span>}
              </button>
            ))}
          </div>

          {/* ════════════════════════════════════════════════════════════
          TAB: BRANCHES GRID
      ════════════════════════════════════════════════════════════ */}
          {tab === 'branches' && (
            <div>
              {/* Filters */}
              <div className="card mb-3">
                <div className="card-body">
                  <div className="row g-2">
                    <div className="col-md-4"><SearchBar onSearch={setSearch} placeholder="Search branch name, city, code…" /></div>
                    <div className="col-md-2">
                      <select className="form-select" value={staF} onChange={e => setStaF(e.target.value)}>
                        <option value="">All Status</option>
                        {Object.keys(BR_STA).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2"><button className="btn btn-outline-secondary w-100" onClick={() => { setSearch(''); setStaF(''); }}>🔄 Reset</button></div>
                  </div>
                </div>
              </div>

              {loading ? <div className="loading-spinner"><div className="spinner-border text-primary" /></div> :
                !branches.length ? <div className="empty-state"><div className="empty-icon">🏛️</div><h5>No branches added yet</h5><p>Create your first branch to start multi-campus management.</p></div> : (
                  <div className="row g-3">
                    {branches.map(br => (
                      <div key={br.id} className="col-md-6 col-lg-4">
                        <div
                          onClick={() => setSelectedBranch(selectedBranch?.id === br.id ? null : br)}
                          style={{ background: '#fff', borderRadius: 16, boxShadow: selectedBranch?.id === br.id ? '0 0 0 3px #1a3a5c, 0 4px 16px rgba(0,0,0,0.1)' : '0 2px 10px rgba(0,0,0,0.08)', cursor: 'pointer', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.2s' }}>
                          {/* Card header */}
                          <div style={{ background: br.is_headquarters ? 'linear-gradient(135deg, #1a3a5c, #2563a8)' : 'linear-gradient(135deg, #334155, #475569)', padding: '16px 18px', color: '#fff' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div>
                                <div style={{ fontWeight: 800, fontSize: 16 }}>{br.name}</div>
                                <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>📍 {br.city}, {br.state}</div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                                <code style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>{br.code}</code>
                                {br.is_headquarters && <span style={{ background: '#f59e0b', color: '#0f172a', fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 20 }}>HQ</span>}
                              </div>
                            </div>
                            {br.tagline && <div style={{ fontSize: 11, opacity: 0.65, marginTop: 6 }}>{br.tagline}</div>}
                          </div>

                          {/* Card body */}
                          <div style={{ padding: '14px 18px', flex: 1 }}>
                            {/* Stats row */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                              {[['🎓', br.total_students, 'Students'], ['👨‍🏫', br.total_staff, 'Staff'], ['🏫', br.total_classrooms, 'Rooms']].map(([icon, val, label]) => (
                                <div key={label} style={{ textAlign: 'center', background: '#f8fafc', borderRadius: 8, padding: '8px 4px' }}>
                                  <div style={{ fontSize: 14 }}>{icon}</div>
                                  <div style={{ fontWeight: 800, fontSize: 16, color: '#1a3a5c' }}>{val}</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{label}</div>
                                </div>
                              ))}
                            </div>

                            {/* Info */}
                            <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>
                              {br.principal_name && <div style={{ marginBottom: 4 }}>👤 {br.principal_name}</div>}
                              {br.phone && <div style={{ marginBottom: 4 }}>📞 {br.phone}</div>}
                              {br.email && <div style={{ marginBottom: 4 }}>📧 {br.email}</div>}
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                                {br.board && <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{br.board}</span>}
                                {br.medium && <span style={{ background: '#f0fdf4', color: '#15803d', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{br.medium.replace('_medium', '')}</span>}
                                {br.classes_offered && <span style={{ background: '#faf5ff', color: '#7c3aed', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600 }}>{br.classes_offered}</span>}
                              </div>
                            </div>

                            {/* Facilities */}
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                              {[['🏠', 'Hostel', br.has_hostel], ['🚌', 'Transport', br.has_transport], ['📖', 'Library', br.has_library], ['🔬', 'Labs', br.has_labs], ['🏅', 'Sports', br.has_sports_complex], ['🍽️', 'Cafeteria', br.has_cafeteria]].filter(f => f[2]).map(([icon, name]) => (
                                <span key={name} style={{ fontSize: 11, color: '#475569', background: '#f1f5f9', padding: '2px 8px', borderRadius: 20 }}>{icon} {name}</span>
                              ))}
                            </div>
                          </div>

                          {/* Card footer */}
                          <div style={{ padding: '10px 18px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip map={BR_STA} val={br.status} />
                            <div className="d-flex gap-2">
                              <button className="btn btn-sm btn-outline-primary" onClick={e => { e.stopPropagation(); openEditBranch(br); }}>✏️ Edit</button>
                              <button className="btn btn-sm btn-outline-secondary" onClick={e => { e.stopPropagation(); setSelectedBranch(br); setTab('transfers'); }}>🔄</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              {/* Branch Detail Panel */}
              {selectedBranch && branchPerformance && (
                <div className="card mt-4" style={{ border: '2px solid #1a3a5c' }}>
                  <div className="card-header" style={{ background: '#1a3a5c', color: '#fff' }}>
                    <span style={{ fontWeight: 700 }}>📊 {selectedBranch.name} — Performance Summary</span>
                    <button style={{ background: 'transparent', border: 'none', color: '#fff' }} onClick={() => { setSelectedBranch(null); setBranchPerf(null); }}>✕</button>
                  </div>
                  <div className="card-body">
                    <div className="row g-4">
                      <div className="col-md-3">
                        <div style={{ textAlign: 'center', padding: 20, background: '#f8fafc', borderRadius: 12 }}>
                          <div style={{ fontSize: 36, fontWeight: 900, color: '#1a3a5c' }}>{selectedBranch.total_students}</div>
                          <div style={{ fontSize: 13, color: '#64748b' }}>Total Students</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Capacity: {selectedBranch.total_capacity}</div>
                          <div style={{ height: 6, background: '#e2e8f0', borderRadius: 4, marginTop: 8, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${selectedBranch.total_capacity ? (selectedBranch.total_students / selectedBranch.total_capacity * 100) : 0}%`, background: '#1a3a5c', borderRadius: 4 }} />
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{selectedBranch.total_capacity ? Math.round(selectedBranch.total_students / selectedBranch.total_capacity * 100) : 0}% occupancy</div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div style={{ textAlign: 'center', padding: 20, background: '#f0fdf4', borderRadius: 12 }}>
                          <div style={{ fontSize: 36, fontWeight: 900, color: '#15803d' }}>{selectedBranch.total_staff}</div>
                          <div style={{ fontSize: 13, color: '#64748b' }}>Total Staff</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Assigned: {branchPerformance.staff_assignments}</div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div style={{ textAlign: 'center', padding: 20, background: '#eff6ff', borderRadius: 12 }}>
                          <div style={{ fontSize: 36, fontWeight: 900, color: '#1d4ed8' }}>{branchPerformance.transfers_in}</div>
                          <div style={{ fontSize: 13, color: '#64748b' }}>Transfers In</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Transfers out: {branchPerformance.transfers_out}</div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div style={{ padding: 16, background: '#f8fafc', borderRadius: 12 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>🏗️ Infrastructure</div>
                          {[['🏠 Hostel', selectedBranch.has_hostel], ['🚌 Transport', selectedBranch.has_transport], ['📖 Library', selectedBranch.has_library], ['🔬 Labs', selectedBranch.has_labs], ['🏅 Sports Complex', selectedBranch.has_sports_complex], ['🍽️ Cafeteria', selectedBranch.has_cafeteria]].map(([label, val]) => (
                            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                              <span>{label}</span>
                              <span style={{ color: val ? '#065f46' : '#991b1b', fontWeight: 700 }}>{val ? '✅' : '❌'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
          TAB: COMPARISON
      ════════════════════════════════════════════════════════════ */}
          {tab === 'comparison' && (
            <div className="card">
              <div className="card-header"><strong>📊 Inter-Branch Comparison</strong></div>
              {!branches.length ? <div className="empty-state"><div className="empty-icon">📊</div><h5>No branches to compare</h5></div> : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                      <tr><th>Branch</th><th>Code</th><th>City</th><th>Board</th><th>Students</th><th>Staff</th><th>Capacity</th><th>Occupancy</th><th>Classrooms</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {[...branches].sort((a, b) => b.total_students - a.total_students).map((br, i) => (
                        <tr key={br.id} style={{ background: br.is_headquarters ? '#eff6ff' : 'transparent' }}>
                          <td>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>
                              {br.is_headquarters && <span style={{ background: '#f59e0b', color: '#0f172a', fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 20, marginRight: 6 }}>HQ</span>}
                              {br.name}
                            </div>
                            {br.principal_name && <div style={{ fontSize: 11, color: '#94a3b8' }}>👤 {br.principal_name}</div>}
                          </td>
                          <td><code style={{ fontSize: 12, fontWeight: 700 }}>{br.code}</code></td>
                          <td style={{ fontSize: 12 }}>{br.city}, {br.state}</td>
                          <td>{br.board && <Chip map={BOARD_CFG} val={br.board} />}</td>
                          <td>
                            <div style={{ fontWeight: 800, fontSize: 15, color: '#1a3a5c' }}>{br.total_students.toLocaleString()}</div>
                            <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, marginTop: 3, width: 80, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${branches.length ? (br.total_students / Math.max(...branches.map(b => b.total_students))) * 100 : 0}%`, background: '#1a3a5c', borderRadius: 2 }} />
                            </div>
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 700 }}>{br.total_staff}</td>
                          <td style={{ textAlign: 'center' }}>{br.total_capacity || '—'}</td>
                          <td style={{ textAlign: 'center' }}>
                            {br.total_capacity ? (
                              <span style={{ fontWeight: 700, color: (br.total_students / br.total_capacity) > 0.9 ? '#991b1b' : (br.total_students / br.total_capacity) > 0.7 ? '#92400e' : '#065f46' }}>
                                {Math.round(br.total_students / br.total_capacity * 100)}%
                              </span>
                            ) : '—'}
                          </td>
                          <td style={{ textAlign: 'center' }}>{br.total_classrooms}</td>
                          <td><Chip map={BR_STA} val={br.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot style={{ background: '#f1f5f9', fontWeight: 700 }}>
                      <tr>
                        <td colSpan={4} style={{ fontSize: 12 }}>TOTAL ({branches.length} branches)</td>
                        <td style={{ fontWeight: 900, fontSize: 15, color: '#1a3a5c' }}>{branches.reduce((a, b) => a + b.total_students, 0).toLocaleString()}</td>
                        <td style={{ textAlign: 'center', fontWeight: 800 }}>{branches.reduce((a, b) => a + b.total_staff, 0)}</td>
                        <td style={{ textAlign: 'center' }}>{branches.reduce((a, b) => a + (b.total_capacity || 0), 0) || '—'}</td>
                        <td />
                        <td style={{ textAlign: 'center' }}>{branches.reduce((a, b) => a + b.total_classrooms, 0)}</td>
                        <td>{branches.filter(b => b.status === 'active').length} active</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
          TAB: TRANSFERS
      ════════════════════════════════════════════════════════════ */}
          {tab === 'transfers' && (
            <div>
              <div className="card mb-3">
                <div className="card-body">
                  <div className="row g-2">
                    <div className="col-md-3">
                      <select className="form-select" value={trfStaF} onChange={e => setTrfStaF(e.target.value)}>
                        <option value="">All Status</option>
                        {Object.keys(TRF_STA).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <select className="form-select" value={entityF} onChange={e => setEntityF(e.target.value)}>
                        <option value="">All Types</option>
                        <option value="student">Student Transfers</option>
                        <option value="staff">Staff Transfers</option>
                      </select>
                    </div>
                    <div className="col-md-2"><button className="btn btn-outline-secondary w-100" onClick={() => { setTrfStaF(''); setEntityF(''); }}>🔄 Reset</button></div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-header"><span>🔄 Branch Transfers ({transfers.length})</span><button className="btn btn-sm btn-primary" onClick={() => setShowTrfForm(true)}>➕ Request Transfer</button></div>
                {!transfers.length ? <div className="empty-state"><div className="empty-icon">🔄</div><h5>No transfer requests</h5><p>Create a transfer request to move students or staff between branches.</p></div> : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                        <tr><th>Transfer #</th><th>Entity</th><th>Type</th><th>From Branch</th><th>To Branch</th><th>Transfer Date</th><th>Effective Date</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {transfers.map(t => (
                          <tr key={t.id}>
                            <td><code style={{ fontSize: 11, fontWeight: 700 }}>{t.transfer_number}</code></td>
                            <td>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{t.entity_name}</div>
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>{t.entity_code}</div>
                            </td>
                            <td><span className="badge" style={{ background: t.entity_type === 'student' ? '#eff6ff' : '#f0fdf4', color: t.entity_type === 'student' ? '#1d4ed8' : '#15803d', fontSize: 11 }}>{t.entity_type}</span></td>
                            <td style={{ fontSize: 12, fontWeight: 600 }}>{t.from_branch_name}</td>
                            <td style={{ fontSize: 12, fontWeight: 600 }}>{t.to_branch_name}</td>
                            <td style={{ fontSize: 12 }}>{formatDate(t.transfer_date)}</td>
                            <td style={{ fontSize: 12 }}>{t.effective_date ? formatDate(t.effective_date) : '—'}</td>
                            <td style={{ fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.reason}</td>
                            <td><Chip map={TRF_STA} val={t.status} /></td>
                            <td>
                              {t.status === 'pending' && (
                                <div className="d-flex gap-1">
                                  <button className="btn btn-sm btn-outline-success" onClick={() => approveTransfer(t.id)}>✅ Approve</button>
                                  <button className="btn btn-sm btn-outline-danger" onClick={() => rejectTransfer(t.id)}>❌ Reject</button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
          TAB: STAFF ASSIGNMENTS
      ════════════════════════════════════════════════════════════ */}
          {tab === 'staff_assignments' && (
            <div className="card">
              <div className="card-header"><span>👤 Staff Branch Assignments ({staffAssignments.length})</span><button className="btn btn-sm btn-primary" onClick={() => setShowStaffForm(true)}>➕ Assign Staff</button></div>
              {!staffAssignments.length ? <div className="empty-state"><div className="empty-icon">👤</div><h5>No staff assignments</h5></div> : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead style={{ background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
                      <tr><th>Staff Name</th><th>Staff ID</th><th>Branch</th><th>Role</th><th>Primary Branch</th><th>Start Date</th><th>Active</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {staffAssignments.map(sa => (
                        <tr key={sa.id}>
                          <td style={{ fontWeight: 600 }}>{sa.staff_name}</td>
                          <td><code style={{ fontSize: 12 }}>{sa.staff_id}</code></td>
                          <td style={{ fontSize: 12 }}>{sa.branch_name}</td>
                          <td><span className="badge bg-light text-dark" style={{ fontSize: 11 }}>{sa.role_at_branch.replace(/_/g, ' ')}</span></td>
                          <td style={{ textAlign: 'center' }}>{sa.is_primary_branch ? <span style={{ color: '#065f46', fontWeight: 700 }}>✅ Primary</span> : '—'}</td>
                          <td style={{ fontSize: 12 }}>{formatDate(sa.start_date)}</td>
                          <td style={{ textAlign: 'center' }}>{sa.is_active ? <span style={{ color: '#065f46', fontWeight: 700 }}>Active</span> : <span style={{ color: '#991b1b' }}>Inactive</span>}</td>
                          <td>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => { if (window.confirm('Remove assignment?')) branchesAPI.updateStaffAssignment(sa.id, { is_active: false }).then(() => { toast.success('Removed'); load(); }); }}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
          MODALS
      ════════════════════════════════════════════════════════════ */}

          {/* Branch Form Modal */}
          {showBrForm && (
            <Modal title={editBranch ? `✏️ Edit Branch — [${editBranch.code}] ${editBranch.name}` : '➕ Create New Branch'} onClose={() => setShowBrForm(false)} size="xl">
              <form onSubmit={saveBranch}>
                <div className="modal-body">
                  <div className="row g-3">
                    {/* Identity */}
                    <div className="col-12"><div style={{ fontWeight: 700, fontSize: 13, color: '#334155', padding: '4px 0', borderBottom: '1px solid #e2e8f0' }}>🏛️ Branch Identity</div></div>
                    <div className="col-md-5"><label className="form-label fw-semibold">Branch Name *</label><input className="form-control" value={brForm.name} onChange={e => BF('name', e.target.value)} required placeholder="e.g. SchoolERP Pune Branch" /></div>
                    <div className="col-md-2"><label className="form-label fw-semibold">Branch Code *</label><input className="form-control" value={brForm.code} onChange={e => BF('code', e.target.value.toUpperCase())} required placeholder="PNE01" style={{ fontWeight: 700, letterSpacing: 2 }} /></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Status</label>
                      <select className="form-select" value={brForm.status} onChange={e => BF('status', e.target.value)}>
                        <option value="active">Active</option><option value="inactive">Inactive</option><option value="coming_soon">Coming Soon</option><option value="closed">Closed</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-semibold">Headquarters?</label>
                      <div className="mt-2"><label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}><input type="checkbox" checked={brForm.is_headquarters} onChange={e => BF('is_headquarters', e.target.checked)} /> This is HQ</label></div>
                    </div>
                    <div className="col-12"><label className="form-label fw-semibold">Tagline</label><input className="form-control" value={brForm.tagline} onChange={e => BF('tagline', e.target.value)} placeholder="Short tagline or motto" /></div>

                    {/* Location */}
                    <div className="col-12"><div style={{ fontWeight: 700, fontSize: 13, color: '#334155', padding: '4px 0', borderBottom: '1px solid #e2e8f0' }}>📍 Location Details</div></div>
                    <div className="col-12"><label className="form-label fw-semibold">Address *</label><textarea className="form-control" rows={2} value={brForm.address} onChange={e => BF('address', e.target.value)} required /></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Locality</label><input className="form-control" value={brForm.locality} onChange={e => BF('locality', e.target.value)} /></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">City *</label><input className="form-control" value={brForm.city} onChange={e => BF('city', e.target.value)} required /></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">State *</label><input className="form-control" value={brForm.state} onChange={e => BF('state', e.target.value)} required /></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Pincode *</label><input className="form-control" value={brForm.pincode} onChange={e => BF('pincode', e.target.value)} required /></div>

                    {/* Contact */}
                    <div className="col-12"><div style={{ fontWeight: 700, fontSize: 13, color: '#334155', padding: '4px 0', borderBottom: '1px solid #e2e8f0' }}>📞 Contact</div></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Phone *</label><input className="form-control" value={brForm.phone} onChange={e => BF('phone', e.target.value)} required /></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Alternate Phone</label><input className="form-control" value={brForm.alternate_phone} onChange={e => BF('alternate_phone', e.target.value)} /></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Email</label><input type="email" className="form-control" value={brForm.email} onChange={e => BF('email', e.target.value)} /></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Website</label><input className="form-control" value={brForm.website} onChange={e => BF('website', e.target.value)} placeholder="https://" /></div>

                    {/* Administration */}
                    <div className="col-12"><div style={{ fontWeight: 700, fontSize: 13, color: '#334155', padding: '4px 0', borderBottom: '1px solid #e2e8f0' }}>👔 Administration</div></div>
                    <div className="col-md-4"><label className="form-label fw-semibold">Principal Name</label><input className="form-control" value={brForm.principal_name} onChange={e => BF('principal_name', e.target.value)} /></div>
                    <div className="col-md-4"><label className="form-label fw-semibold">Principal Email</label><input type="email" className="form-control" value={brForm.principal_email} onChange={e => BF('principal_email', e.target.value)} /></div>
                    <div className="col-md-4"><label className="form-label fw-semibold">Principal Phone</label><input className="form-control" value={brForm.principal_phone} onChange={e => BF('principal_phone', e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label fw-semibold">Vice Principal Name</label><input className="form-control" value={brForm.vice_principal_name} onChange={e => BF('vice_principal_name', e.target.value)} /></div>
                    <div className="col-md-6"><label className="form-label fw-semibold">Admin Manager</label><input className="form-control" value={brForm.admin_manager_name} onChange={e => BF('admin_manager_name', e.target.value)} /></div>

                    {/* Academic */}
                    <div className="col-12"><div style={{ fontWeight: 700, fontSize: 13, color: '#334155', padding: '4px 0', borderBottom: '1px solid #e2e8f0' }}>📚 Academic Profile</div></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Board</label>
                      <select className="form-select" value={brForm.board} onChange={e => BF('board', e.target.value)}>
                        <option value="">Select Board</option><option value="CBSE">CBSE</option><option value="ICSE">ICSE</option><option value="IGCSE">IGCSE</option><option value="IB">IB</option><option value="state">State Board</option>
                      </select>
                    </div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Medium</label>
                      <select className="form-select" value={brForm.medium} onChange={e => BF('medium', e.target.value)}>
                        <option value="">Select Medium</option><option value="english">English Medium</option><option value="hindi">Hindi Medium</option><option value="bilingual">Bilingual</option><option value="regional">Regional Medium</option>
                      </select>
                    </div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Classes Offered</label><input className="form-control" value={brForm.classes_offered} onChange={e => BF('classes_offered', e.target.value)} placeholder="e.g. KG to Class 12" /></div>
                    <div className="col-md-3"><label className="form-label fw-semibold">Established Year</label><input type="number" className="form-control" value={brForm.established_year} onChange={e => BF('established_year', e.target.value)} placeholder="e.g. 1998" /></div>
                    <div className="col-md-4"><label className="form-label fw-semibold">Affiliation Number</label><input className="form-control" value={brForm.affiliation_number} onChange={e => BF('affiliation_number', e.target.value)} /></div>

                    {/* Stats */}
                    <div className="col-md-2"><label className="form-label fw-semibold">Total Capacity</label><input type="number" className="form-control" value={brForm.total_capacity} onChange={e => BF('total_capacity', +e.target.value)} min={0} /></div>
                    <div className="col-md-2"><label className="form-label fw-semibold">Total Students</label><input type="number" className="form-control" value={brForm.total_students} onChange={e => BF('total_students', +e.target.value)} min={0} /></div>
                    <div className="col-md-2"><label className="form-label fw-semibold">Total Staff</label><input type="number" className="form-control" value={brForm.total_staff} onChange={e => BF('total_staff', +e.target.value)} min={0} /></div>
                    <div className="col-md-2"><label className="form-label fw-semibold">Classrooms</label><input type="number" className="form-control" value={brForm.total_classrooms} onChange={e => BF('total_classrooms', +e.target.value)} min={0} /></div>

                    {/* Infrastructure & Permissions */}
                    <div className="col-12"><div style={{ fontWeight: 700, fontSize: 13, color: '#334155', padding: '4px 0', borderBottom: '1px solid #e2e8f0' }}>🏗️ Infrastructure & Branch Permissions</div></div>
                    <div className="col-12">
                      <div className="row g-2">
                        {[['has_hostel', '🏠 Hostel'], ['has_transport', '🚌 Transport'], ['has_library', '📖 Library'], ['has_labs', '🔬 Labs'], ['has_sports_complex', '🏅 Sports Complex'], ['has_cafeteria', '🍽️ Cafeteria']].map(([k, label]) => (
                          <div key={k} className="col-md-2">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '8px 12px', background: brForm[k] ? '#f0fdf4' : '#f8fafc', borderRadius: 8, border: `1px solid ${brForm[k] ? '#86efac' : '#e2e8f0'}` }}>
                              <input type="checkbox" checked={brForm[k]} onChange={e => BF(k, e.target.checked)} />{label}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="row g-2 mt-2">
                        {[['can_manage_own_fees', '💰 Manage Own Fees'], ['can_manage_own_curriculum', '📚 Manage Curriculum'], ['can_manage_own_staff', '👤 Manage Own Staff']].map(([k, label]) => (
                          <div key={k} className="col-md-3">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '8px 12px', background: brForm[k] ? '#eff6ff' : '#f8fafc', borderRadius: 8, border: `1px solid ${brForm[k] ? '#93c5fd' : '#e2e8f0'}` }}>
                              <input type="checkbox" checked={brForm[k]} onChange={e => BF(k, e.target.checked)} />{label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowBrForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : editBranch ? '💾 Update Branch' : '🏛️ Create Branch'}</button>
                </div>
              </form>
            </Modal>
          )}

          {/* Transfer Form Modal */}
          {showTrfForm && (
            <Modal title="🔄 Request Branch Transfer" onClose={() => setShowTrfForm(false)} size="lg">
              <form onSubmit={saveTransfer}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-4"><label className="form-label fw-semibold">Transfer Type *</label>
                      <select className="form-select" value={trfForm.entity_type} onChange={e => TF('entity_type', e.target.value)}>
                        <option value="student">Student Transfer</option><option value="staff">Staff Transfer</option>
                      </select>
                    </div>
                    <div className="col-md-4"><label className="form-label fw-semibold">Entity ID *</label><input type="number" className="form-control" value={trfForm.entity_id} onChange={e => TF('entity_id', e.target.value)} required placeholder="Student/Staff ID" /></div>
                    <div className="col-md-4"><label className="form-label fw-semibold">Adm / Employee No.</label><input className="form-control" value={trfForm.entity_code} onChange={e => TF('entity_code', e.target.value)} /></div>
                    <div className="col-12"><label className="form-label fw-semibold">Name *</label><input className="form-control" value={trfForm.entity_name} onChange={e => TF('entity_name', e.target.value)} required placeholder="Full name of student or staff" /></div>
                    <div className="col-md-6"><label className="form-label fw-semibold">From Branch *</label>
                      <select className="form-select" value={trfForm.from_branch} onChange={e => TF('from_branch', e.target.value)} required>
                        <option value="">Select Branch</option>
                        {branches.map(b => <option key={b.id} value={b.id}>[{b.code}] {b.name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6"><label className="form-label fw-semibold">To Branch *</label>
                      <select className="form-select" value={trfForm.to_branch} onChange={e => TF('to_branch', e.target.value)} required>
                        <option value="">Select Branch</option>
                        {branches.filter(b => b.id !== +trfForm.from_branch).map(b => <option key={b.id} value={b.id}>[{b.code}] {b.name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4"><label className="form-label fw-semibold">Transfer Date *</label><input type="date" className="form-control" value={trfForm.transfer_date} onChange={e => TF('transfer_date', e.target.value)} required /></div>
                    <div className="col-md-4"><label className="form-label fw-semibold">Effective Date</label><input type="date" className="form-control" value={trfForm.effective_date} onChange={e => TF('effective_date', e.target.value)} /></div>
                    <div className="col-12"><label className="form-label fw-semibold">Reason for Transfer *</label><textarea className="form-control" rows={3} value={trfForm.reason} onChange={e => TF('reason', e.target.value)} required placeholder="Reason for the transfer request…" /></div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowTrfForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Submitting…' : '🔄 Submit Transfer Request'}</button>
                </div>
              </form>
            </Modal>
          )}

          {/* Staff Assignment Form Modal */}
          {showStaffForm && (
            <Modal title="👤 Assign Staff to Branch" onClose={() => setShowStaffForm(false)} size="md">
              <form onSubmit={saveStaffAssign}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12"><label className="form-label fw-semibold">Branch *</label>
                      <select className="form-select" value={staffForm.branch} onChange={e => setStaffForm(f => ({ ...f, branch: e.target.value }))} required>
                        <option value="">Select Branch</option>
                        {branches.map(b => <option key={b.id} value={b.id}>[{b.code}] {b.name}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4"><label className="form-label fw-semibold">Staff ID *</label><input type="number" className="form-control" value={staffForm.staff_id} onChange={e => setStaffForm(f => ({ ...f, staff_id: e.target.value }))} required /></div>
                    <div className="col-md-8"><label className="form-label fw-semibold">Staff Name *</label><input className="form-control" value={staffForm.staff_name} onChange={e => setStaffForm(f => ({ ...f, staff_name: e.target.value }))} required /></div>
                    <div className="col-md-6"><label className="form-label fw-semibold">Role at Branch</label>
                      <select className="form-select" value={staffForm.role_at_branch} onChange={e => setStaffForm(f => ({ ...f, role_at_branch: e.target.value }))}>
                        <option value="principal">Principal</option><option value="vice_principal">Vice Principal</option><option value="admin_manager">Admin Manager</option><option value="teacher">Teacher</option><option value="clerk">Clerk</option><option value="other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6"><label className="form-label fw-semibold">Start Date *</label><input type="date" className="form-control" value={staffForm.start_date} onChange={e => setStaffForm(f => ({ ...f, start_date: e.target.value }))} required /></div>
                    <div className="col-12">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '10px 14px', background: staffForm.is_primary_branch ? '#eff6ff' : '#f8fafc', borderRadius: 8, border: `1px solid ${staffForm.is_primary_branch ? '#93c5fd' : '#e2e8f0'}` }}>
                        <input type="checkbox" checked={staffForm.is_primary_branch} onChange={e => setStaffForm(f => ({ ...f, is_primary_branch: e.target.checked }))} />
                        🏛️ This is the staff member's primary branch
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowStaffForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Assigning…' : '👤 Assign to Branch'}</button>
                </div>
              </form>
            </Modal>
          )}
        </>
      )}

      {/* branch_registration end */}


      {/* branch user access start */}
      {/* ─────────────────────────────────────────────────────────────
    BRANCH USER ACCESS
───────────────────────────────────────────────────────────── */}

      {mainTab === 'branch_user_access' && (
        <div className="card border-0 shadow-sm">

          {/* Header */}
          <div
            className="card-header"
            style={{
              background: '#1a3a5c',
              color: '#fff',
              fontWeight: 700
            }}
          >
            <i className="bi bi-people me-2"></i>
            Branch User Access
          </div>

          <div className="card-body">

            {/* Filters */}
            <div className="row g-3 align-items-end mb-4">

              <div className="col-md-2">
                <label className="form-label fw-semibold">
                  Country
                </label>

                <select
                  className="form-select"
                  value={accessFilters.country}
                  onChange={(e) =>
                    setAccessFilters(f => ({
                      ...f,
                      country: e.target.value
                    }))
                  }
                >
                  <option value="">Select</option>
                  <option value="India">India</option>
                  <option value="USA">USA</option>
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label fw-semibold">
                  State
                </label>

                <select
                  className="form-select"
                  value={accessFilters.state}
                  onChange={(e) =>
                    setAccessFilters(f => ({
                      ...f,
                      state: e.target.value
                    }))
                  }
                >
                  <option value="">Select</option>
                  <option value="Telangana">Telangana</option>
                  <option value="Andhra Pradesh">Andhra Pradesh</option>
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label fw-semibold">
                  District
                </label>

                <select
                  className="form-select"
                  value={accessFilters.district}
                  onChange={(e) =>
                    setAccessFilters(f => ({
                      ...f,
                      district: e.target.value
                    }))
                  }
                >
                  <option value="">Select</option>
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label fw-semibold">
                  Sub District
                </label>

                <select
                  className="form-select"
                  value={accessFilters.sub_district}
                  onChange={(e) =>
                    setAccessFilters(f => ({
                      ...f,
                      sub_district: e.target.value
                    }))
                  }
                >
                  <option value="">Select</option>
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label fw-semibold">
                  User Type
                </label>

                <select
                  className="form-select"
                  value={accessFilters.user_type}
                  onChange={(e) =>
                    setAccessFilters(f => ({
                      ...f,
                      user_type: e.target.value
                    }))
                  }
                >
                  <option value="">Select</option>
                  <option value="SUPERADMIN">SUPERADMIN</option>
                  <option value="BRANCHADMIN">BRANCHADMIN</option>
                  <option value="STAFF">STAFF</option>
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label fw-semibold">
                  Branch Name
                </label>

                <select
                  className="form-select"
                  value={accessFilters.branch}
                  onChange={(e) =>
                    setAccessFilters(f => ({
                      ...f,
                      branch: e.target.value
                    }))
                  }
                >
                  <option value="">Select</option>
                  <option value="Hyderabard">Hyderabard</option>
                  <option value="Vizag">Vizag</option>
                  <option value="Warangal">Warangal</option>
                </select>
              </div>

            </div>

            {/* Assigned / Unassigned */}
            <div className="row mb-4">

              <div className="col-md-6 mx-auto">

                <div
                  className="d-flex justify-content-center gap-5"
                  style={{
                    border: '1px solid #dee2e6',
                    borderRadius: 10,
                    padding: '10px 20px',
                    background: '#f8fafc'
                  }}
                >

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="accessType"
                      checked={accessFilters.access_type === 'assigned'}
                      onChange={() =>
                        setAccessFilters(f => ({
                          ...f,
                          access_type: 'assigned'
                        }))
                      }
                    />

                    <label className="form-check-label fw-semibold">
                      Assigned
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="accessType"
                      checked={accessFilters.access_type === 'unassigned'}
                      onChange={() =>
                        setAccessFilters(f => ({
                          ...f,
                          access_type: 'unassigned'
                        }))
                      }
                    />

                    <label className="form-check-label fw-semibold">
                      Un-Assigned
                    </label>
                  </div>

                </div>

              </div>

            </div>

            {/* Top Actions */}
            <div className="d-flex justify-content-between align-items-center mb-3">

              <h6
                style={{
                  fontWeight: 700,
                  color: '#1a3a5c',
                  margin: 0
                }}
              >
                Branch User Access
              </h6>

              <div className="d-flex gap-2">

                <button className="btn btn-outline-primary btn-sm">
                  <i className="bi bi-search"></i>
                </button>

                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() =>
                    setAccessFilters({
                      country: '',
                      state: '',
                      district: '',
                      sub_district: '',
                      user_type: '',
                      branch: '',
                      access_type: 'assigned'
                    })
                  }
                >
                  <i className="bi bi-arrow-clockwise"></i>
                </button>

              </div>

            </div>

            {/* Table */}
            <div
              className="table-responsive"
              style={{
                border: '1px solid #dee2e6',
                borderRadius: 10,
                overflow: 'hidden'
              }}
            >

              <table className="table table-bordered table-hover align-middle mb-0">

                <thead
                  style={{
                    background: '#4f6b8a',
                    color: '#fff',
                    fontSize: 13
                  }}
                >
                  <tr>
                    <th>Branch Name</th>
                    <th>User Type</th>
                    <th>User Name</th>
                    <th>Email Id</th>
                    <th>Dept-Designation</th>
                    <th className="text-center">
                      Is Default Branch
                    </th>
                    <th className="text-center">
                      Is Current Branch
                    </th>
                    <th className="text-center">
                      Unassigned <input type='checkbox' />
                    </th>
                  </tr>
                </thead>

                <tbody>

                  {branchAccessUsers.map(user => (

                    <tr key={user.id}>

                      <td>{user.branch_name}</td>

                      <td>
                        <span
                          className={`badge ${user.user_type === 'SUPERADMIN'
                            ? 'bg-danger'
                            : user.user_type === 'BRANCHADMIN'
                              ? 'bg-primary'
                              : 'bg-success'
                            }`}
                        >
                          {user.user_type}
                        </span>
                      </td>

                      <td style={{ fontWeight: 600 }}>
                        {user.user_name}
                      </td>

                      <td>{user.email}</td>

                      <td>{user.department}</td>

                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={user.is_default_branch}
                          readOnly
                        />
                      </td>

                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={user.is_current_branch}
                          readOnly
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={user.unassigned}
                          readOnly
                        />
                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

            {/* Footer Buttons */}
            <div className="d-flex justify-content-center gap-3 mt-4">

              <button className="btn btn-primary px-4 content-center">
                <i className="bi bi-save me-2"></i>
                Save
              </button>

              <button
                className="btn btn-outline-secondary px-4 content-center"
                onClick={() =>
                  setAccessFilters({
                    country: '',
                    state: '',
                    district: '',
                    sub_district: '',
                    user_type: '',
                    branch: '',
                    access_type: 'assigned'
                  })
                }
              >
                <i className="bi bi-x-circle me-2"></i>
                Clear
              </button>

            </div>

          </div>

        </div>
      )}
      {/* branch user access end */}

    </div>
  );
}
